import React, { useState } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Text,
  Box,
  Divider,
  HStack,
  IconButton,
  Switch,
  Select,
  Collapse,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { LuBraces, LuClock } from 'react-icons/lu';
import {  } from 'react-icons/bs';
import { FiSave } from 'react-icons/fi';
import VariablePickerDrawer from '../VariablePickerDrawer';

export default function CallWhatsappFlowProperties({ nodeConfig, setNodeConfig, updateNodeData, nodes = [] }) {
  const [varPickerOpen, setVarPickerOpen] = useState(false);
  const [activeVarIndex, setActiveVarIndex] = useState(null);

  const kvPairs = nodeConfig.kvPairs || [];

  const openVarPicker = (index) => {
    setActiveVarIndex(index);
    setVarPickerOpen(true);
  };

  const handleVarSelect = (varPath) => {
    const token = `{{${varPath}}}`;
    const newPairs = [...kvPairs];
    newPairs[activeVarIndex] = {
      ...newPairs[activeVarIndex],
      value: (newPairs[activeVarIndex].value || '') + token,
    };
    setNodeConfig({ ...nodeConfig, kvPairs: newPairs });
    setVarPickerOpen(false);
  };

  const addKvPair = () => {
    setNodeConfig({ ...nodeConfig, kvPairs: [...kvPairs, { key: '', value: '' }] });
  };

  const removeKvPair = (index) => {
    setNodeConfig({ ...nodeConfig, kvPairs: kvPairs.filter((_, i) => i !== index) });
  };

  const updateKvPair = (index, field, value) => {
    const newPairs = [...kvPairs];
    newPairs[index] = { ...newPairs[index], [field]: value };
    setNodeConfig({ ...nodeConfig, kvPairs: newPairs });
  };

  const isValid = nodeConfig.flowId?.trim() && nodeConfig.bodyText?.trim();

  return (
    <VStack spacing={4} align="stretch" mt={2}>
      {/* Step Name */}
      <FormControl>
        <FormLabel fontSize="sm">Step Name</FormLabel>
        <Input
          size="sm"
          value={nodeConfig.label || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
          placeholder="e.g., Send Onboarding Flow"
        />
      </FormControl>

      <Divider />

      {/* Flow ID */}
      <FormControl isRequired>
        <FormLabel fontSize="sm">Flow ID</FormLabel>
        <Input
          size="sm"
          value={nodeConfig.flowId || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, flowId: e.target.value })}
          placeholder="Enter WhatsApp Flow ID"
          fontFamily="mono"
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          The ID of the WhatsApp Flow to trigger
        </Text>
      </FormControl>

      {/* Body Text */}
      <FormControl isRequired>
        <FormLabel fontSize="sm">Body Text</FormLabel>
        <Textarea
          size="sm"
          value={nodeConfig.bodyText || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, bodyText: e.target.value })}
          placeholder="e.g., Please complete the form below"
          rows={3}
          fontSize="sm"
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          Text shown to the customer above the flow
        </Text>
      </FormControl>

      <Divider />

      {/* Key-Value Pairs */}
      <Box>
        <HStack justify="space-between" mb={2}>
          <Box>
            <Text fontSize="sm" fontWeight="medium">Flow Parameters</Text>
            <Text fontSize="xs" color="gray.500">Key-value pairs sent as flow data</Text>
          </Box>
          <Button
            size="xs"
            colorScheme="green"
            leftIcon={<AddIcon boxSize="10px" />}
            onClick={addKvPair}
          >
            Add
          </Button>
        </HStack>

        <VStack spacing={2} align="stretch">
          {kvPairs.map((pair, index) => (
            <HStack key={index} spacing={2}>
              <Input
                flex={1}
                size="sm"
                placeholder="Key"
                value={pair.key || ''}
                onChange={(e) => updateKvPair(index, 'key', e.target.value)}
              />
              <Box position="relative" flex={1}>
                <Input
                  size="sm"
                  placeholder="Value or {{variable}}"
                  value={pair.value || ''}
                  fontFamily="mono"
                  pr="8"
                  onChange={(e) => updateKvPair(index, 'value', e.target.value)}
                />
                <IconButton
                  aria-label="Pick variable"
                  icon={<LuBraces />}
                  size="xs"
                  variant="ghost"
                  color="var(--brand-primary)"
                  _hover={{ bg: 'blue.50' }}
                  position="absolute"
                  right={1}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={1}
                  onClick={() => openVarPicker(index)}
                />
              </Box>
              <IconButton
                size="sm"
                variant="ghost"
                colorScheme="red"
                icon={<DeleteIcon />}
                aria-label="Remove"
                onClick={() => removeKvPair(index)}
              />
            </HStack>
          ))}
          {kvPairs.length === 0 && (
            <Box
              textAlign="center"
              py={3}
              border="2px dashed"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Text fontSize="xs" color="gray.400">No parameters added yet</Text>
            </Box>
          )}
        </VStack>
      </Box>

      <Divider />

      {/* Timeout */}
      <Box>
        <HStack justify="space-between" mb={3}>
          <Box>
            <Text fontSize="sm" fontWeight="medium">Timeout / No Response</Text>
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              Route when the customer doesn't complete the flow
            </Text>
          </Box>
          <Switch
            isChecked={nodeConfig.timeoutEnabled || false}
            onChange={(e) => setNodeConfig({ ...nodeConfig, timeoutEnabled: e.target.checked })}
          />
        </HStack>

        <Collapse in={nodeConfig.timeoutEnabled} animateOpacity>
          <VStack
            spacing={3}
            align="stretch"
            p={3}
            bg="gray.50"
            border="1px"
            borderColor="gray.300"
            borderRadius="md"
          >
            <HStack spacing={1} fontSize="xs" color="gray.700">
              <LuClock size={14} />
              <Text fontWeight="medium">Wait time before routing to timeout path</Text>
            </HStack>

            <HStack>
              <Box flex={1}>
                <FormLabel fontSize="xs">Wait Time</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  value={nodeConfig.timeoutWaitTime || 30}
                  onChange={(e) =>
                    setNodeConfig({ ...nodeConfig, timeoutWaitTime: parseInt(e.target.value) || 1 })
                  }
                />
              </Box>
              <Box flex={1}>
                <FormLabel fontSize="xs">Unit</FormLabel>
                <Select
                  size="sm"
                  value={nodeConfig.timeoutWaitUnit || 'minutes'}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, timeoutWaitUnit: e.target.value })}
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </Select>
              </Box>
            </HStack>

            <Box fontSize="xs" color="gray.700" bg="gray.100" px={2} py={1.5} borderRadius="md">
              <Text fontWeight="medium" mb={1}>Note:</Text>
              <Text>
                If the flow is not completed within {nodeConfig.timeoutWaitTime || 30}{' '}
                {nodeConfig.timeoutWaitUnit || 'minutes'}, route through the "Timeout" path.
              </Text>
            </Box>
          </VStack>
        </Collapse>
      </Box>

      <Divider />

      <Alert status="info" borderRadius="md" fontSize="xs">
        <AlertIcon boxSize={4} />
        <Box>
          <Text fontWeight="medium" mb={0.5}>Output Handles</Text>
          <Text>
            <strong>Completed</strong> — customer submitted the flow
            {nodeConfig.timeoutEnabled && (
              <>
                {' · '}
                <strong>Timeout</strong> — no response within the wait time
              </>
            )}
          </Text>
        </Box>
      </Alert>

      <Button
        size="sm"
        colorScheme="green"
        leftIcon={<FiSave />}
        onClick={updateNodeData}
        isDisabled={!isValid}
        w="full"
      >
        Update Node
      </Button>

      <VariablePickerDrawer
        isOpen={varPickerOpen}
        onClose={() => setVarPickerOpen(false)}
        onSelect={handleVarSelect}
        flowNodes={nodes}
      />
    </VStack>
  );
}
